import { HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  signal,
} from '@angular/core';

import { GameServer } from '../../models/game-server.model';
import { GameServerService } from './game-server.service';

@Injectable({
  providedIn: 'root',
})
export class ActiveGameServerService {
  private readonly gameServerService =
    inject(GameServerService);

  readonly gameServers =
    signal<GameServer[]>([]);

  readonly activeGameServerId =
    signal<number | null>(null);

  readonly loading = signal(false);
  readonly error = signal(false);

  /**
   * HTTP status of the last failed load, or null when the request never
   * reached the server (network error, server down). Used to explain why
   * synchronization is currently unavailable.
   */
  readonly errorStatus = signal<number | null>(null);

  readonly activeGameServer = computed(() => {
    const activeGameServerId =
      this.activeGameServerId();

    if (activeGameServerId === null) {
      return null;
    }

    return (
      this.gameServers().find(
        (gameServer) =>
          gameServer.id ===
          activeGameServerId,
      ) ?? null
    );
  });

  loadGameServers(): void {
    this.loading.set(true);
    this.error.set(false);
    this.errorStatus.set(null);

    this.gameServerService
      .getGameServers()
      .subscribe({
        next: (gameServers) => {
          this.gameServers.set(gameServers);

          const currentGameServerId =
            this.activeGameServerId();

          const currentGameServerStillExists =
            gameServers.some(
              (gameServer) =>
                gameServer.id ===
                currentGameServerId,
            );

          if (!currentGameServerStillExists) {
            this.activeGameServerId.set(
              gameServers[0]?.id ?? null,
            );
          }

          this.loading.set(false);
        },
        error: (error: unknown) => {
          this.gameServers.set([]);
          this.activeGameServerId.set(null);
          this.loading.set(false);
          this.error.set(true);
          this.errorStatus.set(
            error instanceof HttpErrorResponse
              ? error.status
              : null,
          );
        },
      });
  }

  selectGameServer(
    gameServerId: number,
  ): void {
    const gameServerExists =
      this.gameServers().some(
        (gameServer) =>
          gameServer.id === gameServerId,
      );

    if (!gameServerExists) {
      return;
    }

    this.activeGameServerId.set(
      gameServerId,
    );
  }
}
